import { MAX_FILE_UPLOAD } from "@/config/constants";
import toast from "react-hot-toast";
import { useState, useRef } from "react";

/**
* useFileUpload Hook
* Picks a file, validates it, and handles drag-and-drop.
*
* It does NOT upload anything, and it no longer pretends to. It used to run a
* 2-second fake progress bar on selection — a `setInterval` counting to 100%
* for a request that had not been made, never cleared, so it kept setting state
* after the component unmounted, and it held the Continue button disabled for
* two seconds for nothing. Whoever sends the file owns the progress: the JD
* flow reads real bytes from axios (`useJDUpload`), and the apply flow shows the
* extraction stream's own progress.
*
* @param {number} maxFileSizeMB - Max file size upload validation
* @param {string[]} allowedExtensions - Accepted file extensions (defaults to
*   PDF/DOCX so the JD-upload flow is unaffected; resume flows pass .doc too).
*/
export const useFileUpload = (maxFileSizeMB = MAX_FILE_UPLOAD, allowedExtensions = [".pdf", ".docx"]) => {
  const fileInputRef = useRef(null);

  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Validate file size and type
  const validateFile = (file) => {
    if (file.size > maxFileSizeMB * 1024 * 1024) {
      toast.error(`File size exceeds ${maxFileSizeMB} MB limit`);
      return false;
    }

    const validFormats = [
      "application/pdf",
      "application/msword", // .doc
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx only
    ];
    const fileExtension = "." + file.name.split(".").pop().toLowerCase();
    if (!validFormats.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      toast.error(`Please upload only ${allowedExtensions.join(", ")} files`);
      return false;
    }

    return true;
  };

  // Handle file selection. Returns whether the file was accepted, so a caller
  // can start its own upload only for a file that passed validation.
  const handleFileSelect = (file) => {
    if (!validateFile(file)) return false;
    setSelectedFile(file);
    return true;
  };

  // Drag & Drop Handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files[0]) handleFileSelect(files[0]);
  };

  // Trigger file input click
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // Forget the chosen file
  const resetUpload = () => {
    setSelectedFile(null);
  };

  return {
    fileInputRef,
    isDragging,
    selectedFile,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    triggerFileInput,
    handleFileSelect,
    resetUpload,
    setSelectedFile,
  };
};
 